import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import type { TooltipPayloadEntry, TooltipValueType } from 'recharts';
import { ChartContainer, ChartLegend, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { t } from '@/i18n';
import type { FinancialMonth, PayrollMonth } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, formatAxis, formatPLN, formatPct, orderedLegendContent } from './chart-utils';

/**
 * "Ile z bazy kosztowej to ludzie" — payroll employer cost next to the invoice
 * cost base, so the owner can see what share of spend is people.
 *
 * DOUBLE-COUNT GUARD, the whole point of this chart's shape: a B2B
 * contractor's cost arrives as a purchase invoice and is ALREADY inside
 * `FinancialMonth.costNet`. It is therefore never added to `employerCost` —
 * it is *carved out* of the invoice cost and shown as its own labelled
 * segment, with the rest of the invoices as `otherInvoiceCost`. The three
 * segments sum to `employerCost + costNet` exactly once:
 *
 *   employerCost (payroll, not invoiced) + b2bCost + (costNet - b2bCost)
 *
 * Adding `b2bCostYtd` to `employerCostYtd` anywhere would double-count it.
 */
const STACK_ORDER = ['employerCost', 'b2bCost', 'otherInvoiceCost'] as const;

interface Row {
  month: string;
  employerCost: number;
  b2bCost: number;
  otherInvoiceCost: number;
  total: number;
  peopleShare: number | null;
}

function CostBaseTooltip({
  active,
  payload,
  label,
  year,
  config,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry<TooltipValueType, number | string>[];
  label?: number | string;
  year: number;
  config: ChartConfig;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as Row | undefined;
  if (!row) return null;

  return (
    <div className="border-border/50 bg-background grid min-w-[15rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium">{`${String(label)} ${year}`}</div>
      <div className="grid gap-1.5">
        {payload.map((item) => (
          <div key={String(item.dataKey)} className="flex w-full items-center gap-2">
            <div className="size-2.5 shrink-0 rounded-[2px]" style={{ background: item.color }} />
            <span className="text-muted-foreground">
              {config[String(item.dataKey)]?.label ?? String(item.name)}
            </span>
            <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
              {formatPLN(Number(item.value))} PLN
            </span>
          </div>
        ))}
        <div className="border-border/50 flex w-full items-center gap-2 border-t pt-1.5">
          <div className="size-2.5 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground">{t('dashboard.people.costBaseTotal')}</span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {formatPLN(row.total)} PLN
          </span>
        </div>
        {row.peopleShare != null && (
          <div className="flex w-full items-center gap-2">
            <div className="size-2.5 shrink-0" aria-hidden="true" />
            <span className="text-muted-foreground">{t('dashboard.people.peopleShare')}</span>
            <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
              {formatPct(row.peopleShare)}%
            </span>
          </div>
        )}
      </div>
      <p className="text-muted-foreground border-border/50 mt-0.5 max-w-[16rem] border-t pt-1.5 leading-snug">
        {t('dashboard.people.b2bTooltipNote')}
      </p>
    </div>
  );
}

export function PeopleVsInvoiceCostChart({
  payrollMonths,
  financialMonths,
  year,
}: {
  payrollMonths: PayrollMonth[];
  financialMonths: FinancialMonth[];
  year: number;
}) {
  const payrollByMonth = new Map(payrollMonths.map((m) => [m.month, m]));

  // Cut the axis at the last month that actually has data on either side.
  // Zero-filling to December would draw a cliff to zero for months that simply
  // haven't happened yet.
  const lastPayroll = payrollMonths.at(-1)?.month ?? 0;
  const lastFinancial = financialMonths.reduce(
    (acc, m) => (m.costNet !== 0 ? m.month : acc),
    0,
  );
  const lastMonth = Math.max(lastPayroll, lastFinancial);

  const rows: Row[] = financialMonths
    .filter((m) => m.month <= lastMonth)
    .map((m) => {
      const p = payrollByMonth.get(m.month);
      const employerCost = p?.employerCost ?? 0;
      // Clamp: b2b is a subset of costNet, but the two aggregates are computed
      // independently, so a rounding/attribution edge must not push the
      // "other" segment negative and inflate the stack.
      const b2bCost = Math.min(p?.b2bCost ?? 0, m.costNet);
      const otherInvoiceCost = Math.max(m.costNet - b2bCost, 0);
      const total = employerCost + b2bCost + otherInvoiceCost;
      return {
        month: MONTH_LABELS[m.month - 1],
        employerCost,
        b2bCost,
        otherInvoiceCost,
        total,
        peopleShare: total > 0 ? ((employerCost + b2bCost) / total) * 100 : null,
      };
    });

  const config: ChartConfig = {
    employerCost: { label: t('dashboard.people.payrollCost'), color: 'var(--chart-5)' },
    b2bCost: { label: t('dashboard.people.b2bCost'), color: 'var(--chart-3)' },
    otherInvoiceCost: { label: t('dashboard.people.otherInvoiceCost'), color: 'var(--chart-4)' },
  };

  if (rows.length === 0) {
    return (
      <ChartCard title={t('dashboard.people.costBaseTitle')}>
        <p className="text-muted-foreground py-12 text-center text-sm">
          {t('dashboard.people.noPayroll')}
        </p>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={t('dashboard.people.costBaseTitle')}>
      <p className="text-muted-foreground mb-3 text-xs">{t('dashboard.people.costBaseHint')}</p>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={rows}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip content={<CostBaseTooltip config={config} year={year} />} />
          <ChartLegend content={orderedLegendContent([...STACK_ORDER])} />
          {STACK_ORDER.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="costbase"
              fill={`var(--color-${key})`}
              maxBarSize={28}
              stroke="var(--card)"
              strokeWidth={2}
              radius={i === STACK_ORDER.length - 1 ? [4, 4, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </ChartContainer>
      <p className="text-muted-foreground mt-3 text-xs leading-snug">
        {t('dashboard.people.b2bFootnote')}
      </p>
    </ChartCard>
  );
}
